import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Clock, ArrowRight, EnvelopeSimple } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicSubscribeSuccess() {
  const [sp] = useSearchParams();
  const sid = sp.get("session_id");
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const [info, setInfo] = useState({ email: "", role_hint: "company", user_exists: false });

  useEffect(() => {
    if (!sid) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async () => {
      for (let i = 0; i < 30 && !cancelled; i++) {
        try {
          const { data } = await axios.get(`${BACKEND_URL}/api/public/payments/status/${sid}`);
          setInfo({ email: data.email || "", role_hint: data.role_hint || "company", user_exists: !!data.user_exists });
          if (data.payment_status === "paid") { setStatus("paid"); return; }
          if (data.status === "expired" || data.status === "failed") { setStatus("failed"); return; }
        } catch {}
        setAttempts(i + 1);
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) setStatus("timeout");
    };
    poll();
    return () => { cancelled = true; };
  }, [sid]);

  const registerUrl = `/register?email=${encodeURIComponent(info.email || "")}`;

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-blue-50 via-white to-emerald-50">
      <Card className="max-w-lg w-full p-8 text-center shadow-2xl">
        <div className="flex justify-center mb-6"><Logo size={56} /></div>

        {status === "checking" && (
          <>
            <Clock size={64} weight="duotone" className="mx-auto text-blue-600 animate-pulse" />
            <h2 className="text-2xl font-extrabold mt-4" style={{ fontFamily: "Manrope" }}>Confirmando pagamento…</h2>
            <p className="text-slate-600 mt-2 text-sm">Tentativa {attempts}/30</p>
            <p className="text-xs text-slate-500 mt-4">Não feche esta página. PIX pode levar até 30 segundos.</p>
          </>
        )}

        {status === "paid" && (
          <>
            <CheckCircle size={72} weight="fill" className="mx-auto text-emerald-600" />
            <h2 className="text-3xl font-extrabold mt-4" style={{ fontFamily: "Manrope" }}>Pagamento confirmado! 🎉</h2>
            <p className="text-slate-700 mt-3">
              Recebemos seu pagamento. Uma confirmação foi enviada para <strong>{info.email || "seu e-mail"}</strong>.
            </p>

            {info.user_exists ? (
              <>
                <div className="mt-6 rounded-lg bg-emerald-50 border-2 border-emerald-200 p-4 text-left">
                  <p className="font-bold text-emerald-900 text-sm">✅ Sua assinatura já está ativa!</p>
                  <p className="text-sm text-emerald-800 mt-1">
                    Faça login com seu e-mail e senha para acessar o app.
                  </p>
                </div>
                <Link to="/login"><Button data-testid="go-login-btn" className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white h-12 gap-2 font-bold">
                  Entrar agora <ArrowRight size={18} />
                </Button></Link>
              </>
            ) : (
              <>
                <div className="mt-6 rounded-lg bg-blue-50 border-2 border-blue-200 p-4 text-left">
                  <p className="font-bold text-blue-900 text-sm flex items-center gap-2">
                    <EnvelopeSimple size={18} weight="fill" /> Último passo: crie sua conta
                  </p>
                  <p className="text-sm text-blue-800 mt-2">
                    Cadastre-se usando o mesmo e-mail <strong>{info.email}</strong> — sua assinatura será ativada automaticamente.
                  </p>
                </div>
                <Link to={registerUrl}><Button data-testid="go-register-btn" className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white h-12 gap-2 font-bold">
                  Criar conta agora <ArrowRight size={18} />
                </Button></Link>
                <p className="text-xs text-slate-500 mt-3">
                  Já tem conta? <Link to="/login" className="text-blue-700 font-bold hover:underline">Entrar</Link>
                </p>
              </>
            )}
          </>
        )}

        {(status === "failed" || status === "timeout" || status === "error") && (
          <>
            <XCircle size={64} weight="fill" className="mx-auto text-red-600" />
            <h2 className="text-2xl font-extrabold mt-4" style={{ fontFamily: "Manrope" }}>
              {status === "timeout" ? "Aguardando confirmação" : "Não foi possível confirmar"}
            </h2>
            <p className="text-slate-600 mt-2 text-sm">
              {status === "timeout"
                ? "O pagamento pode levar mais tempo (especialmente PIX). Assim que compensar, você receberá um e-mail e poderá criar sua conta."
                : "Tente novamente ou entre em contato pelo WhatsApp."}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-2">
              <Link to="/assinar" className="flex-1"><Button variant="outline" className="w-full">Tentar novamente</Button></Link>
              <Link to="/" className="flex-1"><Button variant="outline" className="w-full">Voltar ao início</Button></Link>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
